import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import EquipementBlock from './EquipementBlock';
import { depasseLaBorne, MESSAGE_TROP_D_EQUIPEMENTS } from '../../utils/borneEquipements';
import BlocPV from './equipements/BlocPV';
import PhotoSerialCapture from './equipements/PhotoSerialCapture';
import { BlocBatterie, BlocBallon, BlocPAC, BlocRadiateur, etiquettePhotoRadiateur }
  from './equipements/BlocsSimples';

/**
 * Étape 2 : quels équipements ont été posés, et avec quelles caractéristiques.
 *
 * Chaque type a sa propre saisie, dans son propre fichier : les champs d'un
 * photovoltaïque n'ont rien à voir avec ceux d'un radiateur, et les mélanger dans un
 * seul écran de 600 lignes rendait chaque ajout risqué pour les quatre autres.
 */

// Le titre est une CLE, pas un texte : il est resolu au rendu, quand la langue est
// connue. Le range ici en francais l'aurait fige a la lecture du module.
const TYPE_META_INSTALL = {
  PV: { cle: 'equip_pv', secours: 'Panneaux photovoltaïques (PV)', color: 'text-amber-500 bg-amber-50' },
  Batterie: { cle: 'equip_batterie', secours: 'Batterie de stockage', color: 'text-violet-500 bg-violet-50' },
  Ballon: { cle: 'equip_ballon', secours: 'Ballon eau chaude (ECS)', color: 'text-cyan-500 bg-cyan-50' },
  PAC: { cle: 'equip_pac', secours: 'Pompe à chaleur (PAC)', color: 'text-orange-500 bg-orange-50' },
  Radiateur: { cle: 'equip_radiateur', secours: 'Radiateurs électriques', color: 'text-rose-500 bg-rose-50' },
};

// Une etiquette par piece a identifier. Une PAC split en a deux — unite interieure et
// unite exterieure — parce que ce sont deux machines avec deux numeros distincts.
export const PHOTO_LABELS = {
  PV: ['Étiquette onduleur / micro-onduleur', 'Étiquette panneau PV'],
  Batterie: ['Étiquette N° série batterie'],
  Ballon: ['Étiquette N° série ballon'],
  PAC: ['Étiquette unité extérieure', 'Étiquette unité intérieure'],
  Radiateur: ['Étiquette N° série radiateur'],
};

/**
 * Les etiquettes attendues pour un equipement DONNE, et non pour son type.
 *
 * Le radiateur est le seul cas ou le nombre de photos depend de ce que le poseur a
 * saisi : un chantier porte autant de radiateurs qu'il en a ajoute, et chacun a sa
 * propre etiquette de numero de serie. Une seule photo pour trois machines laisserait
 * deux d'entre elles sans aucune preuve de pose.
 *
 * Les quatre autres types gardent leur liste fixe, mot pour mot.
 */
export function etiquettesPhotos(code, v) {
  if (code !== 'Radiateur') return PHOTO_LABELS[code] || [];
  return (v.unites || []).map((_, rang) => etiquettePhotoRadiateur(rang));
}

const BLOCS = {
  PV: BlocPV, Batterie: BlocBatterie, Ballon: BlocBallon,
  PAC: BlocPAC, Radiateur: BlocRadiateur,
};

export default function StepEquipement({
  equipements, upE, toggleE, referenceChantier, setReferenceChantier,
  enabledEquipements, step2Valid, setStep, t, refus = '',
}) {
  // `t` n'est pas fourni par tous les appelants pendant le portage : on retombe sur le
  // texte français écrit sur place plutôt que d'afficher un identifiant technique.
  const trad = t || ((cle, secours) => secours || cle);
  // Le bouton suit `step2Valid`, qui inclut la borne (page) ; ici, le texte qui l'explique.
  const tropDEquipements = depasseLaBorne(enabledEquipements);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="step-asset">
      <h2 className="text-lg font-semibold text-neutral-900">
        {trad('step2_title', 'Équipement installé')}
      </h2>
      <p className="text-[12px] text-neutral-500">
        Sélectionnez et complétez les équipements posés sur ce chantier.
      </p>

      {/* Lot L4 : le refus du serveur nomme l'équipement à corriger. Il se relit ICI,
          là où l'on corrige, et s'efface dès qu'un numéro change (`useRefusDuServeur`). */}
      {refus && (
        <div className="p-2.5 rounded-md bg-red-50 border border-red-100 text-red-700 text-[12px]"
             role="alert" data-testid="refus-equipements">{refus}</div>
      )}

      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <Label className="text-[11px] text-neutral-500">
          {trad('field_reference_chantier', 'Référence chantier')} *
        </Label>
        <Input
          className="h-11 text-[14px]"
          value={referenceChantier}
          onChange={(e) => setReferenceChantier(e.target.value)}
          spellCheck="true"
          data-testid="inst-ref-chantier"
        />
      </div>

      {Object.entries(BLOCS).map(([code, Bloc]) => (
        <EquipementBlock
          key={code}
          code={code}
          meta={{ ...TYPE_META_INSTALL[code],
            label: trad(TYPE_META_INSTALL[code].cle, TYPE_META_INSTALL[code].secours) }}
          enabled={equipements[code].enabled}
          onToggle={() => toggleE(code)}
          data-testid={`equip-${code}`}
        >
          <Bloc v={equipements[code]} upE={upE} t={trad} />
          <div className="pt-3 mt-3 border-t border-neutral-100">
            <p className="text-[11px] font-medium text-neutral-700 mb-2">
              {trad('lbl_photos_section', 'Photos étiquettes N° série (obligatoires)')}
            </p>
            <PhotoSerialCapture
              photos={equipements[code].photos_serial || []}
              onChange={(suivantes) => upE(code, 'photos_serial', suivantes)}
              labels={etiquettesPhotos(code, equipements[code])}
              tid={`photos-${code}`}
            />
          </div>
        </EquipementBlock>
      ))}

      {enabledEquipements.length === 0 && (
        <p className="text-[12px] text-amber-600 text-center" data-testid="no-equip-warning">
          Sélectionnez au moins un équipement pour continuer.
        </p>
      )}

      {/* Lot L4 : la borne s'explique JUSTE AU-DESSUS de Suivant. On ne la dépasse
          qu'en ajoutant des radiateurs, en bas de page : un bandeau en tête serait
          plusieurs écrans plus haut, et Suivant resterait grisé sans un mot. */}
      {tropDEquipements && (
        <div className="p-2.5 rounded-md bg-red-50 border border-red-100 text-red-700 text-[12px]"
             role="alert" data-testid="trop-d-equipements">{MESSAGE_TROP_D_EQUIPEMENTS}</div>
      )}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1 h-12 text-[14px]" onClick={() => setStep(1)} data-testid="step2-back-btn">
          <ArrowLeft className="w-5 h-5 mr-1.5" /> Retour
        </Button>
        <Button className="flex-1 h-12 text-[15px]" disabled={!step2Valid} onClick={() => setStep(3)} data-testid="step2-next-btn">
          {trad('btn_next', 'Suivant')} <ArrowRight className="w-5 h-5 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
